"use client";

import { useState, useCallback } from "react";
import Link from "next/link";
import Image from "next/image";
import { CheckCircle, X, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useAppDispatch } from "@/app/lib/features/redux/redux.hook";
import useAuth from "@/app/lib/features/auth/auth.hook";
import { performReviewAction } from "@/app/lib/features/productReview/productReview.thunk";
import { ReviewListItem } from "@/app/lib/features/productReview/productReview.types";
import { Button } from "@/app/components/shadcn/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/app/components/shadcn/tooltip";
import StarRating from "@/app/components/elements/StarRating";
import FallbackImage from "@/app/components/elements/FallbackImage";
import { trackGeneric } from "@/app/lib/tracking/interactions";

interface ReviewCardProps {
  review: ReviewListItem;
}

const COMMENT_LIMIT = 200;

const ReviewCard = ({ review }: ReviewCardProps) => {
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useAuth();
  const [likeCount, setLikeCount] = useState(review.like_count);
  const [userAction, setUserAction] = useState<'like' | 'complaint' | null>(review.user_action);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const images = review.images ?? [];
  const openLightbox = (index: number) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  const prevImage = () => setLightboxIndex((i) => (i === null ? null : (i - 1 + images.length) % images.length));
  const nextImage = () => setLightboxIndex((i) => (i === null ? null : (i + 1) % images.length));

  const isLong = (review.comment?.length ?? 0) > COMMENT_LIMIT;
  const displayedComment = isLong && !isExpanded
    ? review.comment.slice(0, COMMENT_LIMIT) + "..."
    : review.comment;

  /**
   * Beğeni ve şikayet AYNI alanın (user_action) iki değeri — biri seçilince
   * diğeri düşer. Sayaç bu yüzden tek tek butonlarda hesaplanamaz:
   *
   *   beğen (1) → şikayet et → beğeni geri alınmalı (0), ama eskiden
   *   handleReport likeCount'a hiç dokunmuyordu; sayı 1'de kalıyor, sonra
   *   tekrar beğenilince `userAction !== 'like'` olduğu için 2'ye çıkıyordu.
   *
   * Artık iki buton da tek akıştan geçiyor ve sayı API'nin döndüğü değerden
   * yazılıyor — istek zaten like_count/complaint_count/user_action döndürüyor,
   * yerel tahmine gerek yok. Alan beklenen tipte gelmezse (API'de olabiliyor)
   * yerel hesap yedeğe düşüyor.
   */
  const runAction = useCallback(
    async (actionType: 'like' | 'complaint') => {
      if (!isAuthenticated) {
        toast.error("Bu işlem için giriş yapmalısınız");
        return;
      }

      if (isLoading) return;

      const failMessage =
        actionType === 'like'
          ? "Beğeni işlemi başarısız oldu"
          : "Şikayet işlemi başarısız oldu";

      setIsLoading(true);
      try {
        const result = await dispatch(
          performReviewAction({ reviewId: review.id, actionType }),
        );

        if (!performReviewAction.fulfilled.match(result)) {
          toast.error(failMessage);
          return;
        }

        // Aynı butona ikinci basış = geri alma.
        const nextAction = userAction === actionType ? null : actionType;
        const { like_count, user_action } = result.payload;

        // Yalnızca yeni işlem (geri alma değil) track edilir.
        if (userAction !== actionType) {
          trackGeneric(
            actionType === 'like' ? "review_helpful" : "review_report",
            "click",
          );
        }

        setUserAction(user_action !== undefined ? user_action : nextAction);
        setLikeCount((prev) => {
          if (typeof like_count === "number") return like_count;
          // Yedek: beğeni yalnızca 'like' durumuna girerken artar, ondan
          // çıkarken azalır — şikayete geçiş de bir çıkıştır.
          const wasLiked = userAction === 'like';
          const willBeLiked = nextAction === 'like';
          if (wasLiked === willBeLiked) return prev;
          return willBeLiked ? prev + 1 : Math.max(prev - 1, 0);
        });

        toast.success(
          actionType === 'like' ? "Beğeni kaydedildi" : "Şikayet kaydedildi",
        );
      } catch {
        toast.error(failMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, review.id, isAuthenticated, isLoading, userAction],
  );

  const handleLike = useCallback(() => runAction('like'), [runAction]);
  const handleReport = useCallback(() => runAction('complaint'), [runAction]);

  // Find info items
  const variantInfo = review.info.find(i => i.key === 'variant')?.value;
  const weightInfo = review.info.find(i => i.key === 'weight')?.value;
  const heightInfo = review.info.find(i => i.key === 'height')?.value;
  const dateInfo = review.info.find(i => i.key === 'created_at')?.value;

  // Find action buttons
  const likeButton = review.action_buttons.find(btn => btn.key === 'like');
  const reportButton = review.action_buttons.find(btn => btn.key === 'complaint');

  // Check if verified
  const isVerified = review.status.key === 'verified' || review.status.key === 'approved';

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <StarRating rating={review.rating} size="sm" />
          <div className="flex items-center gap-2 mt-1">
            <span className="text-sm font-medium">{review.review_customer_info}</span>
            {isVerified && (
              <span className="flex items-center gap-1 text-xs text-green-600">
                <CheckCircle className="w-3 h-3" />
                Onaylanmış Alıcı
              </span>
            )}
            {dateInfo && <span className="text-xs text-gray-500">{dateInfo}</span>}
          </div>
          {(variantInfo || weightInfo || heightInfo) && (
            <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
              {variantInfo && <span>Beden: {variantInfo}</span>}
              {weightInfo && <span>• Kilo: {weightInfo}</span>}
              {heightInfo && <span>• Boy: {heightInfo}</span>}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isAuthenticated ? (
            <>
              {likeButton && (
                <Button
                  size="sm"
                  variant={userAction === 'like' ? "default" : "outline"}
                  onClick={handleLike}
                  disabled={isLoading}
                  className={userAction === 'like' ? "bg-green-600 hover:bg-green-700" : ""}
                >
                  <Image 
                    src={likeButton.icon} 
                    alt="Helpful" 
                    width={16} 
                    height={16} 
                    className="mr-1" 
                  />
                  ({likeCount})
                </Button>
              )}
              {reportButton && (
                <Button 
                  size="sm" 
                  variant={userAction === 'complaint' ? "destructive" : "ghost"} 
                  onClick={handleReport}
                  disabled={isLoading}
                >
                  <Image 
                    src={reportButton.icon} 
                    alt="Report" 
                    width={16} 
                    height={16} 
                  />
                </Button>
              )}
            </>
          ) : (
            <>
              {likeButton && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <Link href="/auth/login">
                        <Button
                          size="sm"
                          variant="outline"
                        >
                          <Image 
                            src={likeButton.icon} 
                            alt="Helpful" 
                            width={16} 
                            height={16} 
                            className="mr-1" 
                          />
                          ({likeCount})
                        </Button>
                      </Link>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Bu işlem için giriş yapmalısınız</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {reportButton && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <Link href="/auth/login">
                        <Button 
                          size="sm" 
                          variant="ghost"
                        >
                          <Image 
                            src={reportButton.icon} 
                            alt="Report" 
                            width={16} 
                            height={16} 
                          />
                        </Button>
                      </Link>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Bu işlem için giriş yapmalısınız</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </>
          )}
        </div>
      </div>
      
      {/* Review Images */}
      {images.length > 0 && (
        <div className="flex gap-2 mb-3">
          {images.map((image, index) => (
            <div
              key={index}
              onClick={() => openLightbox(index)}
              className="relative w-20 h-20 rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition-opacity bg-gray-100"
            >
              <FallbackImage
                src={image}
                alt={`Review image ${index + 1}`}
                fill
                className="object-cover"
                fallbackClassName="object-contain p-4 opacity-40"
              />
            </div>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90"
          onClick={closeLightbox}
        >
          <button
            type="button"
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white/80 hover:text-white"
          >
            <X size={28} />
          </button>
          {images.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); prevImage(); }}
                className="absolute left-4 text-white/80 hover:text-white"
              >
                <ChevronLeft size={36} />
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); nextImage(); }}
                className="absolute right-4 text-white/80 hover:text-white"
              >
                <ChevronRight size={36} />
              </button>
            </>
          )}
          <div
            className="relative max-w-[90vw] max-h-[90vh] w-full h-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <Image
              src={images[lightboxIndex]}
              alt={`Review image ${lightboxIndex + 1}`}
              fill
              className="object-contain"
              unoptimized
            />
          </div>
          {images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
              {images.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setLightboxIndex(i); }}
                  className={`w-2 h-2 rounded-full transition-colors ${i === lightboxIndex ? "bg-white" : "bg-white/40"}`}
                />
              ))}
            </div>
          )}
        </div>
      )}
      
      <div className="mb-3">
        <p className="text-sm text-gray-700 leading-relaxed">
          {displayedComment}
        </p>
        {isLong && (
          <button
            type="button"
            onClick={() => setIsExpanded((v) => !v)}
            className="text-xs font-medium text-gray-500 hover:text-gray-800 mt-1 transition-colors"
          >
            {isExpanded ? "Daha az göster" : "Devamını oku"}
          </button>
        )}
      </div>
      <p className="text-xs text-gray-500">
        <strong>{review.seller.name}</strong> satıcısından alındı
      </p>
    </div>
  );
};

export default ReviewCard;